import React, { useState, useRef, useCallback, useEffect } from 'react';
import './FirstPersonView.css';

interface Snowball {
  id: number;
  progress: number;
}

interface Target {
  id: number;
  x: number;
  y: number;
  type: 'good' | 'bad';
  isHit: boolean;
  image: string;
  vx: number; // horizontal velocity for waddle
  vy: number; // vertical velocity for waddle
}

interface FirstPersonViewProps {
  onContinue?: () => void;
  onStartAudio?: () => void;
  onStopAudio?: () => void; // new stop audio callback
  onToggleMute?: () => void; // toggle mute callback
  muted?: boolean; // current mute state
}

const FirstPersonView: React.FC<FirstPersonViewProps> = ({ onContinue, onStartAudio, onStopAudio, onToggleMute, muted }) => {
  const [position, setPosition] = useState({ x: 50, y: 50 });
  const [snowballs, setSnowballs] = useState<Snowball[]>([]);
  const [targets, setTargets] = useState<Target[]>([]);
  const [isThrowing, setIsThrowing] = useState(false);
  const [isPointerLocked, setIsPointerLocked] = useState(false);
  const [badTargetsHit, setBadTargetsHit] = useState(0);
  const [hasEntered, setHasEntered] = useState(false);
  const [friendlyFire, setFriendlyFire] = useState(false); // added
  const containerRef = useRef<HTMLElement>(null); // changed from HTMLDivElement for <main>
  const snowballIdRef = useRef(0);
  const targetIdRef = useRef(0);
  const entryOverlayRef = useRef<HTMLDivElement>(null);

  // Add your image URLs here
  const goodImages = [
    '/reindeer.webp',
    '/access.png',
    '/santa.png',
  ];

  const badImages = [
    '/evilelf.webp',
    '/grinch.webp',
      '/frost.png',
    '/marv.png'
  ];

  const generateTarget = (forcedType?: 'good' | 'bad'): Target => {
    const type: 'good' | 'bad' = forcedType ?? (Math.random() < 0.35 ? 'good' : 'bad'); // 35% good, 65% bad
    const x = Math.random() * 80 + 10; // 10-90%
    const y = Math.random() * 80 + 10;
    return {
      id: targetIdRef.current++,
      x,
      y,
      type,
      isHit: false,
      image: (type === 'good' ? goodImages : badImages)[Math.floor(Math.random() * (type === 'good' ? goodImages.length : badImages.length))],
      vx: (Math.random() - 0.5) * 0.08,
      vy: (Math.random() - 0.5) * 0.08,
    };
  };

  // Initialize targets
  useEffect(() => {
    const targetCount = 16;
    setTargets(Array.from({ length: targetCount }, () => generateTarget()));
  }, []);

  // Pointer lock event handlers
  useEffect(() => {
    const handlePointerLockChange = () => {
      setIsPointerLocked(document.pointerLockElement === containerRef.current);
    };

    const handlePointerLockError = () => {
      console.error('Pointer lock failed');
    };

    document.addEventListener('pointerlockchange', handlePointerLockChange);
    document.addEventListener('pointerlockerror', handlePointerLockError);

    return () => {
      document.removeEventListener('pointerlockchange', handlePointerLockChange);
      document.removeEventListener('pointerlockerror', handlePointerLockError);
    };
  }, []);

  // Waddle / movement animation
  useEffect(() => {
    let rafId: number;
    const animate = () => {
      setTargets(prev => prev.map(t => {
        if (t.isHit) return t; // stop moving once hit
        let nx = t.x + t.vx;
        let ny = t.y + t.vy;
        let nvx = t.vx;
        let nvy = t.vy;
        if (nx < 5 || nx > 95) { nvx = -nvx; nx = Math.max(5, Math.min(95, nx)); }
        if (ny < 5 || ny > 95) { nvy = -nvy; ny = Math.max(5, Math.min(95, ny)); }
        return { ...t, x: nx, y: ny, vx: nvx, vy: nvy };
      }));
      rafId = requestAnimationFrame(animate);
    };
    rafId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafId);
  }, []);

  // Replace collision effect with friendly fire logic
  useEffect(() => {
    if (snowballs.length === 0 || friendlyFire) return;
    const crosshairX = 50;
    const crosshairY = 50;
    const activeTargets = targets.filter(t => !t.isHit);
    const toHitIds: number[] = [];
    snowballs.forEach(snowball => {
      if (snowball.progress < 0.95) return;
      activeTargets.forEach(target => {
        const targetScreenX = target.x - position.x + 50;
        const targetScreenY = target.y - position.y + 50;
        const distance = Math.hypot(targetScreenX - crosshairX, targetScreenY - crosshairY);
        if (distance < 5) toHitIds.push(target.id);
      });
    });
    if (toHitIds.length) {
      const goodHits = targets.filter(t => toHitIds.includes(t.id) && t.type === 'good').length;
      if (goodHits > 0) {
        onStopAudio?.();
        setFriendlyFire(true);
        setTimeout(() => { try { if (document.pointerLockElement) document.exitPointerLock(); } catch (_) {} window.location.reload(); }, 2000);
        return;
      }
      const badHits = targets.filter(t => toHitIds.includes(t.id) && t.type === 'bad').length;
      if (badHits) setBadTargetsHit(prev => prev + badHits);
      setTargets(prev => prev.flatMap(t => toHitIds.includes(t.id) ? [generateTarget(t.type)] : [t]));
    }
  }, [snowballs, targets, position, friendlyFire, onStopAudio]);

  const requestPointerLock = () => {
    if (containerRef.current) {
      containerRef.current.requestPointerLock();
    }
  };

  const handleClick = () => {
    if (!isPointerLocked) {
      requestPointerLock();
      return;
    }
    setIsThrowing(true);
    const newSnowball: Snowball = { id: snowballIdRef.current++, progress: 0 };
    setSnowballs(prev => [...prev, newSnowball]);
    setTimeout(() => { setIsThrowing(false); }, 300);
    const animate = () => {
      setSnowballs(prev => prev.map(snowball => {
        if (snowball.id === newSnowball.id) {
          const newProgress = snowball.progress + 0.04;
          if (newProgress >= 1) {
            setTimeout(() => { setSnowballs(prev => prev.filter(s => s.id !== snowball.id)); }, 100);
            return { ...snowball, progress: 1 };
          }
          return { ...snowball, progress: newProgress };
        }
        return snowball;
      }));
      if (newSnowball.progress < 1) requestAnimationFrame(animate);
    };
    animate();
  };

  // Mouse move handler for pointer lock
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current || !isPointerLocked) return;

    // When pointer is locked, use movementX/movementY for relative movement
    const movementX = e.movementX || 0;
    const movementY = e.movementY || 0;

    setPosition(prev => {
      let newX = prev.x + (movementX * 0.05);
      let newY = prev.y + (movementY * 0.05);
      newX = Math.max(0, Math.min(100, newX));
      newY = Math.max(0, Math.min(100, newY));
      return { x: newX, y: newY };
    });
  }, [isPointerLocked]);

  const handleMouseLeave = () => {
    // Don't reset position when pointer is locked
    if (!isPointerLocked) {
      setPosition({ x: 50, y: 50 });
    }
  };

  // Calculate positions for rendering (simplified to avoid unused constant lint warnings)
  const getSnowballPosition = (progress: number) => {
    const currentX = 65 + (50 - 65) * progress; // start 65 -> end 50
    const currentY = 75 + (50 - 75) * progress; // start 75 -> end 50
    const scale = 0.8 - (progress * 0.7);
    return { currentX, currentY, scale, opacity: 1 };
  };

  const handleEnterGame = () => {
    if (hasEntered) return;
    setHasEntered(true);
    try { onStartAudio?.(); } catch (_) {}
    // removed immediate requestPointerLock to avoid pointer lock root document error
    setTimeout(() => { containerRef.current?.focus(); }, 0);
  };

  const instructionsText = (() => {
    if (!hasEntered) return 'Click to enter - Press M to ' + (muted ? 'unmute' : 'mute');
    if (badTargetsHit >= 5) return 'Press Enter to continue - Press M to ' + (muted ? 'unmute' : 'mute');
    return `Please hit ${5 - badTargetsHit} targets to continue - Press M to ${muted ? 'unmute' : 'mute'}`;
  })();

  // Focus the container when badTargetsHit reaches the threshold
  useEffect(() => {
    if (badTargetsHit >= 5 && containerRef.current) {
      containerRef.current.focus();
    }
  }, [badTargetsHit]);

  // Replace previous global key listener with unified handler so M works anytime
  useEffect(() => {
    const handleGlobalKeys = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      if (key === 'm') { e.preventDefault(); onToggleMute?.(); return; }
      if (key === 'enter' && !hasEntered && !friendlyFire) { e.preventDefault(); handleEnterGame(); return; }
      if (key === ' ' && !hasEntered && !friendlyFire) { e.preventDefault(); handleEnterGame(); return; }
      if (key === 'enter' && hasEntered) {
        e.preventDefault();
        if (document.pointerLockElement) document.exitPointerLock();
        onContinue?.();
        return;
      }
      if (key === ' ' && hasEntered) {
        e.preventDefault();
        handleClick();
        return;
      }
    };
    window.addEventListener('keydown', handleGlobalKeys);
    return () => window.removeEventListener('keydown', handleGlobalKeys);
  }, [hasEntered, friendlyFire, onContinue, onToggleMute]);

  useEffect(() => {
    if (hasEntered && containerRef.current) {
      try { containerRef.current.requestPointerLock(); } catch (_) {}
    }
  }, [hasEntered]);

  return (
    <div>
      {!hasEntered && (
        <div
          ref={entryOverlayRef}
          className="entry-overlay"
          role="button"
          tabIndex={0}
          onClick={handleEnterGame}
          onKeyDown={(e) => { if (['Enter',' '].includes(e.key)) { e.preventDefault(); handleEnterGame(); } }}
        >
          <h1>HSC Self Service</h1>
          <div className="enter-button-hint">CLICK OR PRESS ENTER</div>
          {/* Hidden automation bypass element */}
          <div
            id="forAutomation"
            style={{ display: 'none' }}
            onClick={() => {
              try { if (document.pointerLockElement) document.exitPointerLock(); } catch(_){}
              onContinue?.();
            }}
          />
        </div>
      )}
      <main
        ref={containerRef}
        className={`first-person-container ${friendlyFire ? 'friendly-fire-active' : ''}`}
        tabIndex={0}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={() => { if (!hasEntered) { handleEnterGame(); return; } handleClick(); }}
      >
        {hasEntered && !friendlyFire && (
          <>
            <div className="background-image" style={{ backgroundPosition: `${position.x}% ${position.y}%` }} />
            <div className="crosshair" />
            <div className={`hand-overlay ${isThrowing ? 'hand-throwing' : ''}`} />
            <div className="score-display">Bad Targets Hit: {badTargetsHit}</div>
          </>
        )}
        {!friendlyFire && <div className="instructions persistent">{instructionsText}</div>}
        {hasEntered && !friendlyFire && snowballs.map(s => {
          const { currentX, currentY, scale, opacity } = getSnowballPosition(s.progress);
          return (
            <div
              key={s.id}
              className="snowball"
              style={{ left: `${currentX}%`, top: `${currentY}%`, transform: `translate(-50%, -50%) scale(${scale})`, opacity }}
            />
          );
        })}
        {hasEntered && !friendlyFire && targets.map(t => {
          if (t.isHit) return null;
          const screenX = t.x - position.x + 50;
          const screenY = t.y - position.y + 50;
          if (screenX < 0 || screenX > 100 || screenY < 0 || screenY > 100) return null;
          return (
            <div
              key={t.id}
              className={`target ${t.type}-target`}
              style={{ left: `${screenX}%`, top: `${screenY}%`, backgroundImage: `url(${t.image})` }}
            />
          );
        })}
        {hasEntered && friendlyFire && (
          <div className="friendly-fire-overlay">
            <div className="friendly-fire-message">Friendly fire will not be tolerated!</div>
          </div>
        )}
      </main>
    </div>
  );
};

export default FirstPersonView;
